// useTeamSettings — Settings › Team (ART-353): manager mode, label release budgets, Advance Docs Pro. Booking
// discovery shares the tab but is still the band profile's `openToSupport` (useBandProfile).
//
// Saves on change (no Save button), so the mutation is OPTIMISTIC and rolls back on error. Two of the three values
// change what the rest of the app shows — label budgets change a label seat's capabilities, Advance Docs Pro changes
// the navigation document's `advanceDocsPro` — so a save also invalidates the navigation document.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type TeamSettings = components["schemas"]["TeamSettingsView"];
export type TeamSettingsUpdate = components["schemas"]["UpdateTeamSettingsRequest"];
export type ManagerMode = "hands_on" | "hands_off";

/** `GET /bands/{bandId}/settings/team` (band:manage). */
export function useTeamSettings() {
  const { api } = useApp();
  return useBandQuery<TeamSettings>(["settings", "team"], async (bandId) => {
    const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/settings/team", {
      params: { path: { bandId } },
    });
    if (!response.ok || !data) throw new ApiError(response.status, error);
    return data;
  });
}

/** `PATCH /bands/{bandId}/settings/team` — only the fields passed are saved. */
export function useSaveTeamSettings() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = ["band", bandId, "settings", "team"] as const;
  return useBandMutation<TeamSettings, TeamSettingsUpdate, { previous?: TeamSettings }>(
    async (id, body) => {
      const { data, error, response } = await api.PATCH("/api/v1/bands/{bandId}/settings/team", {
        params: { path: { bandId: id } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onMutate: async (body) => {
        await qc.cancelQueries({ queryKey: key });
        const previous = qc.getQueryData<TeamSettings>(key);
        if (previous) {
          qc.setQueryData<TeamSettings>(key, {
            ...previous,
            ...(body.managerMode != null ? { managerMode: body.managerMode } : {}),
            ...(body.labelSeesBudgets != null ? { labelSeesBudgets: body.labelSeesBudgets } : {}),
            ...(body.advanceDocsPro != null ? { advanceDocsPro: body.advanceDocsPro } : {}),
          });
        }
        return { previous };
      },
      onError: (err, _body, ctx) => {
        if (ctx?.previous) qc.setQueryData(key, ctx.previous);
        pushToast(errorMessage(err, "Couldn't save that setting."));
      },
      onSuccess: (data) => {
        qc.setQueryData(key, data);
        void qc.invalidateQueries({ queryKey: ["me", "navigation"] });
      },
    },
  );
}
